import BackIcon from '_assets/img/icons/back-icon.svg';
import Image from '_atoms/Image';
import { TranslatableString } from '_i18n/no/translations';
import UADetector from 'Helper/UADetector';
import useNavigate from 'Hooks/useNavigate';
import useScrollDirection from 'Hooks/useScrollDirection';
import { t } from 'i18next';
import {
    FC,
    isValidElement,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { useLayoutEffect } from 'react';

import './Header.scss';

type Props = {
    title?: ReactNode | TranslatableString;
    showBackButton?: boolean;
    stickyHeaderWithScrollEffect?: boolean;
    backButtonIcon?: string;
    goBack?: () => void;
    secondaryButton?: React.JSX.Element;
};

const Header: FC<Props> = ({
    title,
    showBackButton = true,
    stickyHeaderWithScrollEffect = false,
    backButtonIcon,
    goBack,
    secondaryButton,
}): React.JSX.Element | null => {
    const { isHidden, scrollPosition } = useScrollDirection();
    const { isIPad } = UADetector();
    const navigate = useNavigate();
    const backBtnRef = useRef<HTMLDivElement>(null);
    const secondaryBtnRef = useRef<HTMLDivElement>(null);

    /**
     * Checks whether the back button should be shown.
     * Here is where you can add other custom conditionals,
     * if you need to show or hide the back button
     * @returns {bool}
     */
    const showsBackBtn = (): boolean => {
        const path = window.location.pathname;
        if (path === '/' || path === '/login') {
            return false;
        }

        return !!showBackButton;
    };

    /**
     * header
     */
    const headerClasses = useMemo(() => {
        let headerBaseClass = 'main-page-header';

        if (stickyHeaderWithScrollEffect) {
            if (!isHidden && scrollPosition > 10) {
                headerBaseClass = headerBaseClass + ' showtitle';
            }
        }

        if (isIPad) {
            headerBaseClass = headerBaseClass + ' ipad-title';
        }

        return headerBaseClass;
    }, [isHidden, isIPad, stickyHeaderWithScrollEffect, scrollPosition]);

    /**
     * Title Rendering
     */
    const titleEle = useMemo(() => {
        if (typeof title === 'string') {
            return (
                <>
                    {title.length ? (
                        <>
                            <h1 className="title">
                                {t(title as TranslatableString)}
                            </h1>
                            {showsBackBtn() && !secondaryButton && (
                                <div className="top-right-more-options"></div>
                            )}
                        </>
                    ) : (
                        ''
                    )}
                </>
            );
        }

        if (isValidElement(title)) {
            return title;
        }

        return null;
    }, [title, showsBackBtn]);

    const secondaryButtonEle = useMemo(() => {
        if (secondaryButton) {
            return (
                <div className="secondary-button" ref={secondaryBtnRef}>
                    {secondaryButton}
                </div>
            );
        }

        return null;
    }, [secondaryButton]);

    const handleBack = useCallback(() => {
        if (goBack) {
            goBack();
        } else {
            navigate(-1, { shouldAwaitConfirmation: true });
        }
    }, [goBack, navigate]);

    const backButtonElem = useMemo(() => {
        return (
            <div
                ref={backBtnRef}
                className={`back-button layout-back-button ${
                    isIPad ? 'back-button-ipad' : ''
                }`}
                onClick={handleBack}
            >
                <Image alt={t('Go back')} src={backButtonIcon ?? BackIcon} />
            </div>
        );
    }, [goBack]);

    // This is a hack to make the containers fort the back button and secondary button
    // the same width, so that title is always centered.
    useLayoutEffect(() => {
        if (title && backBtnRef.current && secondaryBtnRef.current) {
            const backBtnWidth = backBtnRef.current.offsetWidth;
            const secondaryBtnWidth = secondaryBtnRef.current.offsetWidth;

            const largestWidth = Math.max(backBtnWidth, secondaryBtnWidth);

            if (backBtnWidth < secondaryBtnWidth) {
                backBtnRef.current.style.width = `${largestWidth}px`;
                secondaryBtnRef.current.style.width = '';
            }
            if (secondaryBtnWidth < backBtnWidth) {
                secondaryBtnRef.current.style.width = `${largestWidth}px`;
                backBtnRef.current.style.width = '';
            }
        }
    }, []);

    /**
     * Header
     */
    const headerEle = useMemo(() => {
        if (showsBackBtn() && title) {
            return (
                <div className={headerClasses}>
                    {showsBackBtn() && backButtonElem}
                    {titleEle}
                    {secondaryButtonEle}
                </div>
            );
        }

        if (showsBackBtn() && secondaryButtonEle) {
            return (
                <div>
                    {backButtonElem}
                    {secondaryButtonEle}
                </div>
            );
        }

        if (showsBackBtn()) {
            return <div>{backButtonElem}</div>;
        }

        if (secondaryButtonEle && title) {
            return (
                <div className={headerClasses}>
                    <div
                        className={`back-button layout-back-button ${
                            isIPad ? 'back-button-ipad' : ''
                        }`}
                    ></div>
                    {titleEle}
                    {secondaryButtonEle}
                </div>
            );
        }

        if (titleEle) {
            return <div className={headerClasses}>{titleEle}</div>;
        }

        return null;
    }, [title, showsBackBtn, handleBack, isIPad]);

    return headerEle;
};

export default Header;
